import {Database, Entities, EntityMap} from "@/store/Database";
import {GlobalID, Owner, OwnerGroup, User} from "@/store/modules/hcs/Model";
import {ActionContext, Module} from "vuex";
import {RootStateType} from "@/store";
import axios, {AxiosInstance} from "axios";
import applyCaseMiddleware from "axios-case-converter";
import calendar from "@/store/modules/hcs/calendar/index";
import controller from "@/store/modules/hcs/controller/index";
import serviceImplementation from "@/store/modules/hcs/serviceImplementation/index";
import dataImplementation from "@/store/modules/hcs/dataImplementation/index";
import dataImplementationsWithMessagesCount from "@/store/modules/hcs/dataImplementationsWithMessagesCount/index";
import dataPropertyImplementation from "@/store/modules/hcs/dataPropertyImplementation/index";
import tradeContract from "@/store/modules/hcs/tradeContract/index";
import tradeContractFormat from "@/store/modules/hcs/tradeContractFormat/index";
import tradeContractFormatGroup from "@/store/modules/hcs/tradeContractFormatGroup/index";
import dataProfile from "@/store/modules/hcs/dataProfile/index";
import dataPropertyProfile from "@/store/modules/hcs/dataPropertyProfile/index";
import serviceProfile from "@/store/modules/hcs/serviceProfile/index";
import user from "@/store/modules/hcs/user/index";
import terminal from "@/store/modules/hcs/terminal/index";
import processImplementation from "@/store/modules/hcs/processImplementation/index";
import eventImplementation from "@/store/modules/hcs/eventImplementation/index";
import triggerImplementation from "@/store/modules/hcs/triggerImplementation/index";
import processProfile from "@/store/modules/hcs/processProfile/index";
import eventProfile from "@/store/modules/hcs/eventProfile/index";
import contractProfileTemplate from "@/store/modules/hcs/contractProfileTemplate/index";
import contractProfile from "@/store/modules/hcs/contractProfile/index";
import processOperationImplementation from "@/store/modules/hcs/processOperationImplementation/index";
import message from "@/store/modules/hcs/message/index";
import serviceRecord from "@/store/modules/hcs/serviceRecord/index";
import owner from "@/store/modules/hcs/owner/index";
import {applyTradeContractPatch} from "@/store/modules/hcs/TradeContractInterceptor";

interface CollectionMap extends EntityMap {
    // ユーザー
    user: User;

    // 事業者
    owner: Owner;

    // 事業者グループ
    ownerGroup: OwnerGroup;
}

export type HcsDatabase = Database<CollectionMap>;

export interface StateType {
    db: HcsDatabase;

    /** ログインユーザー取得 Promise */
    loginUserPromise: Promise<User> | null;

    /** ログインユーザー */
    loginUser: User | null;

    /** ログイン事業者取得 Promise */
    loginOwnerPromise: Promise<Owner> | null;

    /** ログイン事業者 */
    loginOwner: Owner | null;
}

/**
 * サインインしたアカウントの情報
 */
export interface Account {
    /** ユーザー情報 */
    user: User;

    /** 事業者情報 */
    owner: Owner;
}

const db = new Database<CollectionMap>('hcs', ({createEntity}) => {
    // ユーザー
    createEntity('user', 'id');

    // 事業者
    createEntity('owner', 'id');

    // 事業者グループ
    createEntity('ownerGroup', 'id');
});

// const cache = setupCache({
//     maxAge: process.env.NODE_ENV === 'development' ?
//         10 * 1000 : // dev  10秒
//         10 * 1000,  // prod 10秒
//     exclude: {
//         paths: [
//             /^\/sessions/,
//             /^\/owner/,
//             /^\/me/,
//         ]
//     }
// });

axios.defaults.xsrfCookieName = 'CSRF-TOKEN'
axios.defaults.xsrfHeaderName = 'X-CSRF-Token'
axios.defaults.withCredentials = true

let hcsClient = (() => {
    // axiosInstance 生成
    let axiosInstance = axios.create({
        baseURL: process.env.VUE_APP_HCS_ENDPOINT_BASE,
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*'
        },
        responseType: 'json',
        // adapter: cache.adapter,
    });

    // キャメルケース化
    axiosInstance = applyCaseMiddleware(axiosInstance, {
        ignoreHeaders: true
    });

    // trade_contracts API パッチ
    axiosInstance = applyTradeContractPatch(axiosInstance);
    return axiosInstance;
})();


export default {
    namespaced: true,
    state() {
        return {
            db,
            loginUserPromise: null,
            loginUser: null,
            loginOwnerPromise: null,
            loginOwner: null
        };
    },
    getters: {
        entities(state): Entities<CollectionMap> {
            return state.db.entities;
        },
        db(state): HcsDatabase {
            return state.db;
        },
        /**
         * アカウントを取得します
         * ロード中の場合 user もしくは owner が undefined になる可能性があります
         */
        account(state): Partial<Account> {
            return {
                user: state.loginUser || undefined,
                owner: state.loginOwner || undefined
            }
        },
        client(): AxiosInstance {
            return hcsClient;
        }
    },
    actions: {
        /**
         * 基本要素(事業者・事業者グループ)を読み込みます
         *
         * @param context アクションコンテキスト
         * @return 読み込み完了が通知される Promise
         */
        loadOwner(context: ActionContext<StateType, RootStateType>) {
            const s = context.state;
            return hcsClient.get('/assets/owners/')
                .then(response => {
                    const mapping = s.db.compileMapping({
                        owner: 'data*'
                    });
                    s.db.load(response, mapping);
                    return s.db.entities.owner;
                });
        },

        loadOwnerGroup(context: ActionContext<StateType, RootStateType>, payload: {ownerId: GlobalID}) {
            const s = context.state;
            return hcsClient.get(`/owner_groups/owner/${payload.ownerId}`)
                .then(response => {
                    const mapping = s.db.compileMapping({
                        ownerGroup: 'data*'
                    });
                    s.db.load(response, mapping);
                    return s.db.entities.ownerGroup;
                });
        },

        fetchCSRFToken(context: ActionContext<StateType, RootStateType>): Promise<void> {
          return hcsClient.get("/csrf");
        },

        /**
         * HCS にサインインします
         *
         * @param context アクションコンテキスト
         * @param email サインインに使用するメールアドレス
         * @param password サインインに使用するパスワード
         * @param recaptchaToken reCaptcha から取得したトークン
         * @return サインインに成功した結果を受け取る Promise
         */
        signIn(context: ActionContext<StateType, RootStateType>, {email, password, recaptchaToken}: {
            email: string; password: string; recaptchaToken: string;
        }): Promise<Account> {
            return hcsClient.post('/sessions', {email, password, recaptchaToken})
                .then(() => {
                    context.state.loginUserPromise = null;
                    context.state.loginOwnerPromise = null;
                    return this.dispatch('hcs/fetchLoginAccount');
                });
        },

        /**
         * HCS からサインアウトします
         *
         * @param context アクションコンテキスト
         * @return サインアウトに成功した結果を受け取る Promise
         */
        signOut(context: ActionContext<StateType, RootStateType>): Promise<void> {
            return hcsClient.delete('/sessions')
                .then(() => {
                    context.state.loginUserPromise = null;
                    context.state.loginOwnerPromise = null;
                    this.commit('hcs/setLoginUser', {loginUser: null});
                    this.commit('hcs/setLoginOwner', {loginUser: null});
                });
        },

        /**
         * HCS にサインアップします
         *
         * @param context アクションコンテキスト
         * @param email サインアップに使用するメールアドレス
         * @param name サインアップに使用する事業者名
         * @param componentOwner コンポーネント事業者の場合 true そうでない場合 false
         * @param platformOwner プラットフォーム事業者の場合 true そうでない場合 false
         * @param staffName サインアップに使用する担当者名
         * @param recaptchaToken reCaptcha から取得したトークン
         */
        signUp(context: ActionContext<StateType, RootStateType>, {email, name, staffName, componentOwner, platformOwner, recaptchaToken}: {
            email: string; name: string; staffName: string; componentOwner: boolean; platformOwner: boolean; recaptchaToken: string;
        }): Promise<void> {
            return hcsClient.post('/temporary_owners', {email, name, staffName, componentOwner, platformOwner, recaptchaToken})
                .then(() => {
                    context.state.loginUserPromise = null;
                    context.state.loginOwnerPromise = null;
                });
        },

        /**
         * パスワードリセットを要求します
         *
         * @param context アクションコンテキスト
         * @param email パスワードリセットを要求するメールアドレス
         * @param recaptchaToken reCaptcha から取得したトークン
         */
        requestResetPassword(context: ActionContext<StateType, RootStateType>, {email, recaptchaToken}: {
            email: string; recaptchaToken: string;
        }): Promise<void> {
            return hcsClient.post('/reset_password', {email, recaptchaToken})
                .then(() => {
                    context.state.loginUserPromise = null;
                    context.state.loginOwnerPromise = null;
                });
        },

        /**
         * サインインしたアカウント情報を取得します
         *
         * @param context アクションコンテキスト
         * @return アカウント情報の結果を受け取る Promise
         */
        fetchLoginAccount(context: ActionContext<StateType, RootStateType>): Promise<Account> {
            const userPromise = this.dispatch('hcs/fetchLoginUser');
            const ownerPromise = userPromise.then(() => {
                    return this.dispatch('hcs/fetchLoginOwner');
                });
            return Promise.all([userPromise, ownerPromise]).then(([user, owner]) => {
                return { user, owner };
            })
        },

        /**
         * サインインしたユーザー情報を取得します
         *
         * @param context アクションコンテキスト
         * @return ユーザー情報の結果を受け取る Promise
         */
        fetchLoginUser(context: ActionContext<StateType, RootStateType>): Promise<User> {
            if(context.state.loginUserPromise === null) {
                return context.state.loginUserPromise = hcsClient.get('/me').then(response => {
                    this.commit('hcs/setLoginUser', {loginUser: response.data});
                    return response.data;
                });
            }
            return context.state.loginUserPromise;
        },

        /**
         * サインインした事業者情報を取得します
         *
         * @param context アクションコンテキスト
         * @return 事業者情報の結果を受け取る Promise
         */
        fetchLoginOwner(context: ActionContext<StateType, RootStateType>): Promise<Owner> {
            if(context.state.loginOwnerPromise === null) {
                return context.state.loginOwnerPromise = hcsClient.get('/owner').then(response => {
                    this.commit('hcs/setLoginOwner', {loginOwner: response.data});
                    return response.data;
                });
            }
            return context.state.loginOwnerPromise;
        },

        /**
         * 事業者情報を更新します
         *
         * @param context アクションコンテキスト
         * @param owner 事業者情報
         */
        updateOwner(context: ActionContext<StateType, RootStateType>, owner: Owner) {
            return hcsClient.put('/owner', owner)
                .then(response => {
                    context.state.loginOwnerPromise = Promise.resolve(response.data);
                    this.commit('hcs/setLoginOwner', {loginOwner: response.data});
                    return response.data;
                });
        }
    },
    mutations: {
        setLoginUser(state, {loginUser}: {loginUser: User}) {
            state.loginUser = loginUser;
        },
        setLoginOwner(state, {loginOwner}: {loginOwner: Owner}) {
            state.loginOwner = loginOwner;
        }
    },
    modules: {
        calendar,
        controller,
        serviceImplementation,
        dataImplementation,
        dataImplementationsWithMessagesCount,
        dataPropertyImplementation,
        tradeContract,
        tradeContractFormat,
        tradeContractFormatGroup,
        dataProfile,
        dataPropertyProfile,
        serviceProfile,
        user,
        terminal,
        processImplementation,
        eventImplementation,
        triggerImplementation,
        processProfile,
        eventProfile,
        contractProfileTemplate,
        contractProfile,
        processOperationImplementation,
        message,
        serviceRecord,
        owner,
    }
} as Module<StateType, RootStateType>;
